var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Service, { inject as service } from '@ember/service';
import { initDashboardQueryClient } from '@squareup/dashboard-query';
import { dashboardCurrentUser } from '@squareup/dashboard-query/queries/dashboardcurrentuser';
import * as Sentry from '@sentry/ember';
import { redirect } from '@square/dashboard-utilities/utils/window';
import { SquareOneEligibility } from '@squareup/shared-types-protos/squareup/sub2/data';
import { subscriptions } from '@squareup/dashboard-query/queries/subscriptions';
import { ExternalGetOrSetSquareOneEligibilityResponse } from '@squareup/shared-types-protos/squareup/sub2/v3/external/external_subscription_service';
import { getMultipassStatus } from '@square/dashboard-utilities/utils/multipass-status';
import { isTesting } from '@embroider/macros';
const UNAUTHORIZED_STATUS_CODE = 401;
function getGlobalQueryClient() {
  return window.__dashboardQueryClient || window.__dashboardQueryClientV5;
}
export function setGlobalQueryClient(client) {
  // eslint-disable-next-line dashboard/use-utilities-window
  window.__dashboardQueryClient = client;
  // eslint-disable-next-line dashboard/use-utilities-window
  window.__dashboardQueryClientV5 = client;
}
export function clearGlobalQueryClient() {
  delete window.__dashboardQueryClient;
  delete window.__dashboardQueryClientV5;
}
export const queryClientMocks = {
  redirectToSignIn() {
    if (!isTesting()) {
      redirect(`/login?return_to=${window.encodeURIComponent(window.location.pathname)}`);
    }
  }
};
function redirectToSignIn() {
  return queryClientMocks.redirectToSignIn();
}
const destroyedServiceMap = new Map();
let QueryClient = (_class = class QueryClient extends Service {
  constructor(owner) {
    super(owner);
    _initializerDefineProperty(this, "environment", _descriptor, this);
    _initializerDefineProperty(this, "employeeInfo", _descriptor2, this);
    _initializerDefineProperty(this, "userInfo", _descriptor3, this);
    _initializerDefineProperty(this, "features", _descriptor4, this);
    _defineProperty(this, "queryClientInstance", void 0);
    _defineProperty(this, "qcSymbol", void 0);
    this.qcSymbol = Symbol('QueryClient');
    destroyedServiceMap.set(this.qcSymbol, false);
    const qcSymbol = this.qcSymbol;
    const client = getGlobalQueryClient() || initDashboardQueryClient({
      environment: this.environment.name,
      beforeRetry() {
        if (destroyedServiceMap.get(qcSymbol)) {
          return false;
        }
        return null;
      },
      onQueryError: handleRequestError,
      onMutationError: handleRequestError
    });

    // Override cache defaults based on feature flag
    // In test environments, default to false to maintain test stability (avoid stale cached data)
    // In production, default to false to opt into the new behavior gradually
    const enableCaching = this.features.getFlag('dashboard-query-enable-caching-defaults') ?? false;
    if (!enableCaching) {
      const existingOptions = client.getDefaultOptions()?.queries ?? {};
      client.setDefaultOptions({
        queries: {
          ...existingOptions,
          staleTime: 0,
          gcTime: 5 * 60 * 1000
        }
      });
    }
    this.queryClientInstance = client;
  }
  initContext() {
    this.queryClientInstance.setQueryData(dashboardCurrentUser.get().queryKey, {
      user: this.userInfo.data,
      employee: this.employeeInfo.data
    });

    // Pre-populate the cache with SquareOneEligibility data
    if (!this.userInfo.data?.squareOneEligibility) {
      return;
    }
    const squareOneEligibility = SquareOneEligibility.fromPartial(this.userInfo.data?.squareOneEligibility);

    // Pre-populate the cache with a partial ExternalGetOrSetSquareOneEligibilityResponse
    // This cache entry has { staleTime: Infinity }, so manual invalidation is necessary
    // to ensure the cache can later be updated with the full response
    this.queryClientInstance.setQueryData(subscriptions.keys.squareOneEligibility(), ExternalGetOrSetSquareOneEligibilityResponse.fromPartial({
      squareOneEligibility
    }));
  }

  /**
   * Registers the query client instances to the window as globals so that
   * they can be consumed by dashboard-web, which is server-side rendered
   * and cannot be passed props during mount.
   */
  registerGlobals() {
    if (getGlobalQueryClient()) {
      const msg = 'registerGlobals called when a global query client already exists. This is likely a configuration error.';
      // Development and testing environments are not mutually exclusive,
      // and we don't want to throw in tests because some tests run initializers
      // twice.
      if (this.environment.isDevelopment && !this.environment.isTesting) {
        throw new Error(msg);
      } else {
        Sentry.captureMessage(msg);
      }
      return;
    }
    setGlobalQueryClient(this.queryClientInstance);
  }
  get instance() {
    return this.queryClientInstance;
  }

  /** @deprecated - use instance() instead */
  get instanceV5() {
    return this.queryClientInstance;
  }
  willDestroy() {
    // https://tanstack.com/query/latest/docs/reference/QueryClient/#queryclientclear
    // Clears all connected caches, necessary for cleanup on test teardown
    this.queryClientInstance.clear();
    destroyedServiceMap.set(this.qcSymbol, true);
    super.willDestroy();
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "environment", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "employeeInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "userInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "features", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class);
export { QueryClient as default };
function isConnectError(error) {
  if (!error || typeof error !== 'object') {
    return false;
  }
  return 'rawResponse' in error;
}
function handleRequestError(args) {
  const {
    error,
    context
  } = args;
  let url = '';
  if (isConnectError(error)) {
    url = error.rawResponse.url;
  }
  let queryKey = [];
  if ('query' in args) {
    // eslint-disable-next-line unicorn/consistent-destructuring
    queryKey = args.query.queryKey;
  } else {
    queryKey = [];
  }
  if (context.responseStatusCode === UNAUTHORIZED_STATUS_CODE) {
    // Some APIs return a 401 for Non-Square online users, even for authenticated users
    if (queryKey && queryKey.length >= 2 && queryKey[0] === 'weebly' && queryKey[1] === 'accountFeatures') {
      // we know that this query returns a 401 for Non-Square online users, even for authenticated users
      // so we should skip the redirect
      // we've asked the backend to return a non 401 error here
      // adding this temporarily while we push for that change: https://square.slack.com/archives/C06SWGP1ARZ/p1752512784548759
      return;
    }
    getMultipassStatus().catch(() => {
      // If mp status fails the user is not authenticated
      // removing the word "Unauthenticated" from the error message so it doesn't get scrubbed by Sentry
      // https://docs.sentry.io/security-legal-pii/scrubbing/server-side-scrubbing/
      const errorMessageWithoutAuth = error.message.replace('Unauthenticated', 'not logged in');
      Sentry.withScope(scope => {
        scope.setExtras({
          queryKey,
          errorMessage: errorMessageWithoutAuth,
          url
        });

        // capturing message instead of exception because we don't want to log the error to Sentry
        scope.captureMessage('query-client#handleRequestError: Redirecting to login page due to 401');
      });
      redirectToSignIn();
    });
  }
  Sentry.withScope(scope => {
    scope.setExtras({
      ...context,
      url
    });
    Sentry.captureException(error);
  });
}